import { useRoom } from "queries/chat/rooms/hooks";
import { AuthUserRoomMembership } from "queries/chat/rooms/types";
import { useUserKEK } from "queries/member/hooks";
import React, { useEffect, useMemo, useState } from "react";
import { fn } from "ui/lib/helpers";
import { createContainer } from "unstated-next";
import {
  getAuthUserRoomMembershipsByRoomID,
  useChatSecurity,
} from "../helpers";
import { useReplyAttachment } from "../hooks/useReplyAttachment";
import {
  UseSelectedUserReturnProps,
  useSelectedUser,
} from "../hooks/useSelectedUser";
import { ReplyAttachment, Room } from "../types";
import { useChatDots } from "./dots";

interface Section {
  id: string;
  title: string;
  name: string;
  rooms: Room[];
}

export interface MessageData {
  message: {
    roomEncTextBase64?: string;
    text: string;
  };
  [key: string]: any;
}

export interface ChatProviderDefaultProps {
  sort?: "asc" | "desc";
  authUserRoomMemberships?: AuthUserRoomMembership[];
  activePubnubChannel: string;
  activeRoomID: string;
  activeSectionId: string;
  activeRoom?: Room;
  breakOutSectionsByProgram?: boolean;
  channelGroups: string[];
  channels: string[];
  chatGroupRequestString?: string;
  checkRoomHasUnreadMessages: (room: Room) => boolean;
  impersonationEnabled?: boolean;
  messagePinningEnabled?: boolean;
  /**
   * Whether selecting a member opens member info by itself. Unset, it does
   * except on phone and tablet user agents.
   */
  autoOpenMemberInfo?: boolean;
  /** The caller shows a thread's parent above it; leave it out of the list. */
  threadParentShownAbove?: boolean;
  /**
   * Called once a room's newest page has rendered, with its newest
   * timetoken, or null for a room with no messages. Not called while the
   * history the inbox knows of is missing, so a caller can read rooms only
   * once they have been seen. Return false to keep the room's unread dot;
   * with this set, opening a room no longer clears it.
   */
  onNewestShown?: (
    roomId: string,
    newestTimetoken: string | null
  ) => boolean | void;
  /**
   * Replaces the long-press drawer of message actions. Rendered inside the
   * message, so it can read `useMessage` and the message's other hooks.
   */
  messageActionsSheet?: React.ComponentType<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
  }>;
  /** An app's own glyphs for the transcript's Pinned and Highlighted badges. */
  badgeGlyphs?: {
    pin: React.ComponentType<{ className?: string }>;
    highlight: React.ComponentType<{ className?: string }>;
  };
  featureFlags?: {
    memberHoverCard: boolean;
    /** A ⋯ on each message below lg that opens the long-press actions. */
    messageActionsButton?: boolean;
    /** Alt text on image attachments, naming who sent them. */
    describedImages?: boolean;
    /**
     * The phone transcript: unboxed rows, one header per run of messages from
     * an author, touch-sized controls and a full-width date band.
     */
    phoneTranscript?: boolean;
  };
  handleNewMessage: (message: any) => void; // Define a Message type
  loading: boolean;
  mutate: (data: any) => void; // Specify a more precise type for data
  queryChatGroupsByBootcamp?: boolean;
  queryOnboardingRooms?: boolean;
  revalidate: any;
  roomDictionary: Record<string, Room>;
  rooms: Room[];
  sections: Section[];
  updateActiveRoom: (room: Room) => void;
  updateLastReadTimeToken: (roomIds: string[]) => void;
  numHiddenProUsers?: number;
  isThread?: boolean;
  updateActiveRoomID?: (roomID: string) => void;
  pageLoadType?: "auto" | "manual";
  pageLoadSize?: number;
}

export const defaultProps: ChatProviderDefaultProps = {
  sort: "asc",
  checkRoomHasUnreadMessages: () => false,
  updateActiveRoom: () => null,
  activeRoomID: "",
  activeSectionId: "",
  activePubnubChannel: "",
  messagePinningEnabled: true,
  mutate: fn,
  revalidate: fn,
  sections: [],
  channelGroups: [],
  channels: [],
  loading: false,
  rooms: [],
  roomDictionary: {},
  pageLoadType: "auto",
  pageLoadSize: 15,
  updateLastReadTimeToken: fn,
  handleNewMessage: fn,
  featureFlags: {
    memberHoverCard: true,
  },
};

interface ReplyAttachmentProps {
  replyAttachment: ReplyAttachment;
  setReplyAttachment: (attachment: ReplyAttachment) => void;
  resetReplyAttachment: () => void;
}

interface VideoRecorderActiveProps {
  videoRecorderActive: boolean;
  setVideoRecorderActive: (active: boolean) => void;
}

interface UseContainerReturn
  extends ChatProviderDefaultProps,
    VideoRecorderActiveProps,
    ReplyAttachmentProps,
    UseSelectedUserReturnProps {
  isLoading: boolean;
  handleDecryptMessages: (messages: MessageData[]) => Promise<MessageData[]>;
  getAuthUserRoomMembershipsByRoomID?: (roomID: string) => any;
  showChatbotUI: boolean;
  setShowChatbotUI: React.Dispatch<React.SetStateAction<boolean>>;
  showDeletedMessages: boolean;
  setShowDeletedMessages: React.Dispatch<React.SetStateAction<boolean>>;
  showPinnedMessages: boolean;
  setShowPinnedMessages: React.Dispatch<React.SetStateAction<boolean>>;
  showCoachMessages: boolean;
  setShowCoachMessages: React.Dispatch<React.SetStateAction<boolean>>;
}

const useChatSessionUIContainer = () => {
  const [showChatbotUI, setShowChatbotUI] = useState<boolean>(false);
  const [showDeletedMessages, setShowDeletedMessages] =
    useState<boolean>(false);
  const [showPinnedMessages, setShowPinnedMessages] = useState<boolean>(false);
  const [showCoachMessages, setShowCoachMessages] = useState<boolean>(false);
  const [videoRecorderActive, setVideoRecorderActive] = useState(false);

  return {
    showChatbotUI,
    setShowChatbotUI,
    showDeletedMessages,
    setShowDeletedMessages,
    showCoachMessages,
    setShowCoachMessages,
    showPinnedMessages,
    setShowPinnedMessages,
    videoRecorderActive,
    setVideoRecorderActive,
  };
};

const useContainer = (
  props: ChatProviderDefaultProps = defaultProps
): UseContainerReturn => {
  const chatSessionUIProps = useChatSessionUIContainer();

  const { data: userKEKData, isLoading: isLoadingKEK } = useUserKEK();
  const { data: roomData, isLoading: isLoadingRoomData } = useRoom(
    props.activeRoomID as string
  );

  const roomMemberships: AuthUserRoomMembership[] =
    roomData?.authUserRoomMembership ? [roomData?.authUserRoomMembership] : [];

  const kekBase64 = useMemo(() => {
    return userKEKData?.kekBase64 || "";
  }, [userKEKData]);

  const authUserRoomMemberships = getAuthUserRoomMembershipsByRoomID(
    roomMemberships,
    props.activeRoomID
  );

  const allRoomMemberships = useMemo(() => {
    return [...roomMemberships, ...(props.authUserRoomMemberships || [])];
  }, [roomMemberships, props.authUserRoomMemberships]);

  const userEncRoomDEKBase64 = useMemo(() => {
    return authUserRoomMemberships?.userEncRoomDEKBase64 || "";
  }, [authUserRoomMemberships?.userEncRoomDEKBase64]);

  const chatSecurityProps = useChatSecurity({
    kekBase64,
    roomMemberships: allRoomMemberships,
    roomID: props.activeRoomID,
    encDEKBase64: userEncRoomDEKBase64,
    publicDEKBase64: roomData?.room?.isLegacy
      ? undefined
      : roomData?.room?.publicDEKBase64,
  });

  const { clearRoomDots } = useChatDots();
  const replyAttachmentProps = useReplyAttachment();

  const selectedUserProps = useSelectedUser(
    props.activeRoomID,
    props.roomDictionary ?? {},
    props.autoOpenMemberInfo
  );

  useEffect(() => {
    if (!props.activePubnubChannel || props.onNewestShown) return;
    clearRoomDots(props.activePubnubChannel);
  }, [props.activePubnubChannel]);

  return {
    ...chatSecurityProps,
    ...chatSessionUIProps,
    ...replyAttachmentProps,
    ...selectedUserProps,
    ...props,
    isLoading: isLoadingKEK || isLoadingRoomData,
  };
};

export const { useContainer: useChat, Provider: ChatProvider } =
  createContainer(useContainer);
